<script lang="ts">
    import ContentScopeBar from '@amp/web-app-components/src/components/ContentScopeBar/ContentScopeBar.svelte';
    import { createEventDispatcher } from 'svelte';
    import { sanitizeHtml } from '@amp/web-app-components/src/utils/sanitize-html';
    import type { SegmentedControl } from './types';

    const dispatch = createEventDispatcher();

    export let items: SegmentedControl[];
    export let context: string = null;
    export let selectedId: string;
    export let centerControls: boolean = false;
    export let useSmallControls: boolean = true;
    export let clickComponent: any | null = null;
    // let clickComponent = null;

    // Get current index of segment to track sliding animation
    // when more than 2 segments are allowed
    // Initialize to selected item on component mount, so segment
    // begins under the currently selected segment.
    let currentIndex: number = items.findIndex(
        (item) => item.id === selectedId,
    );

    function toggleSelected(itemId: string) {
        dispatch('toggleSelected', itemId);
    }
</script>

<div
    class="search-scope-bar"
    class:desktop-controls-centered={centerControls}
    class:scope-bar-non-material={!context}
    data-testid="segmented-controls"
>
    <ContentScopeBar textAlignment="left">
        {#if context}
            <div
                class="search-scope-bar__results-text"
                data-testid="search-scope-bar-context"
            >
                {@html sanitizeHtml(context, {
                    allowedTags: ['mark'],
                })}
            </div>
        {/if}

        <svelte:fragment slot="controls">
            {#if items?.length}
                <div
                    class="search-scope-bar__pill-container typography-segmented-control"
                    class:search-scope-bar__pill-container--small={useSmallControls}
                    style="--current-index: {currentIndex}"
                    data-testid="segmented-control-container"
                >
                    {#each items as item, idx}
                        <!--
                            A11y warnings on this node can be ignored, as the "real" interactive events
                            happen on children (e.g. ClickAction component).
                            This click event needs to be handled on capture to prevent active segment from
                            "flickering" - otherwise entire bar will go gray for a moment after the click.
                        -->
                        <!-- svelte-ignore a11y-no-static-element-interactions -->
                        <!-- svelte-ignore a11y-click-events-have-key-events -->
                        <div
                            class="search-scope-bar__pill-option"
                            class:search-scope-bar__pill-option--right={idx ===
                                items.length - 1}
                            class:search-scope-bar__pill-option--selected={item.id ===
                                selectedId}
                            on:click|capture={() => (currentIndex = idx)}
                        >
                            {#if !!clickComponent}
                                <svelte:component
                                    this={clickComponent}
                                    action={item.action}
                                >
                                    <span class="search-scope-bar__pill-label">
                                        {item.label}
                                    </span>
                                </svelte:component>
                            {:else}
                                <input
                                    type="radio"
                                    id={item.id}
                                    value={item.id}
                                    aria-label={item.ariaLabel}
                                    checked={item.id === selectedId}
                                    data-testid={item.id}
                                    on:change={() => toggleSelected(item.id)}
                                    on:change={() => (currentIndex = idx)}
                                />
                                <label
                                    aria-hidden="true"
                                    for={item.id}
                                    class="search-scope-bar__pill-label"
                                    data-testid={`${item.id}-label`}
                                    >{item.label}</label
                                >
                            {/if}
                        </div>
                    {/each}
                </div>
            {/if}
        </svelte:fragment>
    </ContentScopeBar>
</div>

<style lang="scss">
    @use '@amp/web-shared-styles/sasskit-stylekit/ac-sasskit-config';
    @use 'ac-sasskit/core/locale' as *;
    @use 'amp/stylekit/core/viewports' as *;
    @use '@amp/web-shared-styles/app/core/globalvars' as *;
    @use '@amp/web-shared-styles/app/core/mixins/focus' as *;

    $search-scope-bar-pill-option-box-shadow-desktop: 0 0 0 0.5px
            var(--segmentedControlSelectedBorder),
        0 1px 1px var(--segmentedControlSelectedShadow1),
        0 2px 1px var(--segmentedControlSelectedShadow2);
    $search-scope-bar-pill-option-box-shadow-desktop-dark: inset 0 0.5px 0 0
        rgba(255, 255, 255, 0.25);
    $search-scope-bar-box-shadow-desktop-prefers-contrast: 0 0 0 0.5px black;

    .search-scope-bar {
        // Do not affect the scope bar position, but need a wrapper class to use global for scope bar styles
        display: contents;

        :global(.content-scope-bar) {
            // Temp styles that can be cleaned up or removed as part of:
            // rdar://89046322 (JMOTW | Search page: support for mobile viewports)
            @media (--range-sidebar-hidden-down) {
                position: unset;
                margin: 12px 0;
            }
        }

        :global(.content-scope-bar__content) {
            @media (--range-xsmall-down) {
                display: none;
            }
        }

        :global(.content-scope-bar__controls) {
            @media (--range-xsmall-down) {
                grid-row: 1;
                grid-column: 1 / 1;
                margin-inline-start: 0;
                margin-inline-end: 0;
            }

            @media (--range-sidebar-visible-up) and (--range-xsmall-down) {
                margin-inline-start: auto;
                margin-inline-end: auto;
            }
        }
    }

    .desktop-controls-centered {
        :global(.content-scope-bar) {
            @media (--sidebar-visible) {
                grid-template-columns: repeat(3, minmax(0, auto));
                margin-top: 24px;
            }

            @media (--range-sidebar-hidden-down) {
                margin-inline-start: var(--bodyGutter);
                margin-inline-end: var(--bodyGutter);
            }
        }

        :global(.content-scope-bar__content) {
            @media (--sidebar-visible) {
                display: none;
            }
        }

        :global(.content-scope-bar__controls) {
            @media (--sidebar-visible) {
                margin-inline-end: 0;
                grid-column: 2;
                width: 100%;
            }
        }
    }

    .scope-bar-non-material {
        :global(.content-scope-bar) {
            background-color: var(--pageBG);
            backdrop-filter: none;
        }
    }

    .search-scope-bar__pill-container {
        padding: 2px;
        min-height: 32px;
        border-radius: 9px;
        background-color: var(--segmentedControlBG);
        overflow: hidden;
        position: relative;
        display: grid;
        grid-auto-flow: column;
        grid-auto-columns: 1fr;

        &:has(:focus-visible) {
            @include focus-shadow;
        }

        // Creates element for sliding animation on mobile
        &::before {
            // calculate element's width based on number of controls
            // use --numControls to specify the total number of controls
            // use --controlOffset to prevent overflow on the last control selected
            // TODO: rdar://135436713 (Web Classical Music: ScopeBarPills: Configure Mobile Animation to support more than 3 segments)
            width: calc(
                (100% / var(--numControls, 2)) - var(--controlOffset, 2px)
            );
            inset: 2px;
            transition: transform 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);

            // use --current-index to calculate stopping position of animation
            transform: translateX(calc(100% * var(--current-index)));

            @media (--sidebar-visible) {
                transition: none;
            }

            @media (prefers-reduced-motion: reduce) {
                transition: none;
            }
        }
    }

    .search-scope-bar__pill-container--small {
        @media (--sidebar-visible) {
            padding: 1px;
            min-height: 22px;
            border-radius: 6px;
            box-shadow: inset 0 0 2px 1px rgba(0, 0, 0, 0.04);

            @media (prefers-color-scheme: dark) {
                box-shadow: inset 0 -0.5px 0 0 rgba(255, 255, 255, 0.2),
                    inset 0 0 1px 0 rgba(255, 255, 255, 0.15);

                @media (prefers-contrast: more) {
                    box-shadow: none;
                }
            }
        }
    }

    .search-scope-bar__pill-container::before,
    .search-scope-bar__pill-option::before {
        border-radius: var(
            --global-border-radius-medium,
            #{$global-border-radius-medium}
        );
        content: '';
        position: absolute;
        pointer-events: none;
    }

    .search-scope-bar__pill-option {
        --linkHoverTextDecoration: none;
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
        text-align: center;
        z-index: var(--z-default);
        border-radius: var(
            --global-border-radius-medium,
            #{$global-border-radius-medium}
        );

        &::before {
            width: 100%;
            height: 100%;
            display: block;
            top: 0;

            @media (--sidebar-visible) {
                border-radius: 6px;
            }
        }

        // Include divider if more than 2 segments are present
        &:not(:first-of-type)::after {
            content: '';
            width: 1px;
            height: 60%;
            background-color: var(--labelDivider);
            position: absolute;
            inset: 0;
            margin-block: auto;
        }

        @media (--sidebar-visible) {
            &:active,
            &:has(:focus-visible) {
                &::before {
                    background-color: var(--segmentedControlActiveBG);
                    box-shadow: $search-scope-bar-pill-option-box-shadow-desktop;

                    @media (prefers-color-scheme: dark) {
                        box-shadow: $search-scope-bar-pill-option-box-shadow-desktop-dark;
                    }

                    @media (prefers-contrast: more) {
                        box-shadow: $search-scope-bar-box-shadow-desktop-prefers-contrast;
                    }
                }
            }
        }

        input {
            position: absolute;
            opacity: 0;
            inset: 0;
            width: 100%;
            height: 100%;
        }

        // Ensure that clickComponent, if used, covers entire pill section
        :global(.click-action) {
            width: 100%;
            height: 100%;
            display: grid;
            place-items: center;
            border-radius: inherit;
        }
    }

    // Hide segment divider if pill is selected or follows a selected pill
    .search-scope-bar__pill-option--selected
        + .search-scope-bar__pill-option::after,
    .search-scope-bar__pill-option.search-scope-bar__pill-option--selected::after {
        background-color: transparent;
    }

    .search-scope-bar__pill-label {
        position: relative;
        display: block;
        width: 100%;
        padding: 5px 12px;

        // ensure injected label styles don't collapse label text
        clip-path: none;
        height: initial;
        z-index: calc(var(--z-default) - 1);

        &::before {
            width: 100%;
            content: '';
            height: 100%;
            position: absolute;
            top: 0;
            inset-inline-start: 0;
            z-index: var(--z-default);
        }

        @media (--sidebar-visible) {
            padding: 0 10px;
        }
    }

    .search-scope-bar__pill-container::before,
    .search-scope-bar__pill-option--selected::before {
        background-color: var(--segmentedControlSelectedBG);
        box-shadow: 0 0 0 0.5px var(--segmentedControlSelectedBorder),
            0 3px 8px var(--segmentedControlSelectedShadow1),
            0 3px 1px var(--segmentedControlSelectedShadow2);

        @media (prefers-contrast: more) {
            box-shadow: $search-scope-bar-box-shadow-desktop-prefers-contrast;
        }
    }

    .search-scope-bar__pill-option--selected {
        &::before {
            display: none;
        }

        .search-scope-bar__pill-label {
            color: var(--segmentedControlSelectedText);

            @media (--range-sidebar-hidden-down) {
                font: var(--body-emphasized);

                // iOS tightens text tracking, when selected, to avoid letters
                // from shifting when font increases to semibold
                letter-spacing: -0.4px;
            }
        }
    }

    .search-scope-bar__pill-option--selected,
    .search-scope-bar__pill-label {
        position: relative;
        z-index: calc(
            var(--z-default) - 1
        ); // Platter needs to be under adjacent label
    }

    .search-scope-bar__results-text {
        color: var(--systemSecondary);

        :global(mark) {
            color: var(--systemPrimary);
            background-color: transparent;
            font-weight: 600; // semibold;
        }
    }
</style>
